import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { User, Package, Heart, LogOut, Star, Mail, LayoutGrid } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { getCustomerOrders, getProducts } from "../lib/api";
import { eur } from "../config";

const STATUS_COLOR = {
  green: "bg-green-50 text-green-700 border-green-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  orange: "bg-amber-50 text-amber-700 border-amber-200",
  yellow: "bg-yellow-50 text-yellow-700 border-yellow-200",
  red: "bg-red-50 text-red-600 border-red-200",
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/compte";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const GoogleBtn = () => (
  <button onClick={googleLogin} data-testid="google-login-btn"
    className="w-full flex items-center justify-center gap-3 border border-dore/40 py-3 text-sm hover:bg-dore/5 transition-colors">
    <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.6 4.9 29.6 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.6 4.9 29.6 3 24 3 16 3 9.1 7.6 6.3 14.7z"/><path fill="#4CAF50" d="M24 45c5.5 0 10.4-1.9 14.2-5.1l-6.6-5.4C29.5 36 26.9 37 24 37c-5.2 0-9.6-3.3-11.2-7.9l-6.6 5.1C9 40.4 15.9 45 24 45z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.6 5.4C41.9 35.9 45 30.4 45 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
    Continuer avec Google
  </button>
);

const AuthForms = () => {
  const { login, register } = useAuth();
  const [tab, setTab] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (tab === "login") { await login(form.email, form.password); toast.success("Connexion réussie !"); }
      else { await register(form); toast.success("Compte créé, bienvenue !"); }
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="auth-forms">
      <div className="text-center mb-8">
        <h1 className="font-display text-3xl text-encre">Mon Compte</h1>
        <p className="font-serif-el italic text-dore">Connectez-vous ou créez votre compte</p>
      </div>
      <div className="flex border border-dore/30 mb-6">
        <button onClick={() => setTab("login")} data-testid="tab-login" className={`flex-1 py-3 text-xs uppercase tracking-widest transition-colors ${tab === "login" ? "bg-noir text-white" : "text-encre"}`}>Connexion</button>
        <button onClick={() => setTab("register")} data-testid="tab-register" className={`flex-1 py-3 text-xs uppercase tracking-widest transition-colors ${tab === "register" ? "bg-noir text-white" : "text-encre"}`}>Inscription</button>
      </div>
      <form onSubmit={submit} className="space-y-4">
        {tab === "register" && <input required placeholder="Prénom et nom" value={form.name} onChange={set("name")} className="w-full border border-dore/30 px-4 py-3 text-sm outline-none focus:border-dore" data-testid="auth-name" />}
        <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className="w-full border border-dore/30 px-4 py-3 text-sm outline-none focus:border-dore" data-testid="auth-email" />
        <input required type="password" placeholder="Mot de passe" value={form.password} onChange={set("password")} className="w-full border border-dore/30 px-4 py-3 text-sm outline-none focus:border-dore" data-testid="auth-password" />
        <button disabled={busy} className="w-full bg-noir text-white py-3.5 text-xs uppercase tracking-widest font-semibold hover:bg-ardoise transition-colors disabled:opacity-50" data-testid="auth-submit">{busy ? "..." : tab === "login" ? "Se connecter" : "Créer mon compte"}</button>
      </form>
      <div className="flex items-center gap-3 my-5"><div className="flex-1 h-px bg-dore/20" /><span className="text-xs text-ardoise/50">ou</span><div className="flex-1 h-px bg-dore/20" /></div>
      <GoogleBtn />
    </div>
  );
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { wishlist } = useCart();
  const [orders, setOrders] = useState([]);
  const [wishProducts, setWishProducts] = useState([]);

  useEffect(() => {
    getCustomerOrders().then((d) => setOrders(d.orders || [])).catch(() => {});
    if (wishlist.length) getProducts().then((all) => setWishProducts(all.filter((p) => wishlist.includes(p.slug)))).catch(() => {});
    else setWishProducts([]);
  }, [wishlist]);

  return (
    <div className="max-w-4xl mx-auto px-6 py-14" data-testid="account-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
        <div className="flex items-center gap-4">
          {user.picture ? <img src={user.picture} alt="" className="w-14 h-14 rounded-full object-cover" /> : <div className="w-14 h-14 rounded-full bg-dore/20 flex items-center justify-center"><User size={26} className="text-dore" /></div>}
          <div>
            <h1 className="font-display text-2xl text-encre">Bonjour, {user.name?.split(" ")[0] || "cliente"}</h1>
            <p className="text-sm text-ardoise/70">{user.email}</p>
          </div>
        </div>
        <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-2 border border-dore/30 px-4 py-2 text-xs uppercase tracking-widest hover:bg-noir hover:text-white transition-colors"><LogOut size={15} /> Déconnexion</button>
      </div>

      {user.is_admin && (
        <Link to="/admin/cj-catalog" data-testid="admin-access-link" className="flex items-center justify-between gap-3 bg-noir text-white px-5 py-4 mb-8 hover:bg-ardoise transition-colors">
          <span className="flex items-center gap-3"><LayoutGrid size={18} className="text-dore" /> <span className="text-sm uppercase tracking-widest">Espace administration</span></span>
          <span className="text-dore text-xs">Gérer les produits CJ →</span>
        </Link>
      )}

      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        <div className="bg-noir text-white p-5"><Star size={20} className="text-dore mb-2" /><p className="font-display text-2xl">{user.loyalty_points}</p><p className="text-xs text-white/60">Points fidélité</p></div>
        <div className="bg-white border border-dore/20 p-5"><Package size={20} className="text-dore mb-2" /><p className="font-display text-2xl text-encre">{orders.length}</p><p className="text-xs text-ardoise/60">Commandes</p></div>
        <div className="bg-rose/30 p-5"><Heart size={20} className="text-encre mb-2" /><p className="font-display text-2xl text-encre">{wishlist.length}</p><p className="text-xs text-ardoise/70">Favoris</p></div>
      </div>

      <h2 className="font-display text-xl text-encre mb-4">Mes commandes</h2>
      {orders.length === 0 ? (
        <p className="text-ardoise text-sm mb-10">Vous n'avez pas encore de commande. <Link to="/boutique" className="text-dore underline">Découvrir la boutique</Link></p>
      ) : (
        <div className="space-y-3 mb-10">
          {orders.map((o) => (
            <div key={o.order_number} className="border border-dore/20 bg-white p-4 flex items-center justify-between flex-wrap gap-3" data-testid="order-row">
              <div className="min-w-0">
                <p className="text-sm font-medium text-encre">{o.order_number}</p>
                <p className="text-xs text-ardoise/60">{new Date(o.created_at).toLocaleDateString("fr-FR")} · {o.items_count} article(s) · {eur(o.total)}</p>
                {o.has_tracking && <p className="text-xs text-ardoise/70 mt-0.5">🚚 {o.shipping_carrier} · {o.tracking_number}</p>}
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-[11px] px-2 py-1 uppercase tracking-wider border ${STATUS_COLOR[o.status_color] || STATUS_COLOR.blue}`} data-testid="order-status-badge">{o.status_label}</span>
                <Link to={`/compte/commandes/${o.order_number}`} data-testid="order-detail-link" className="text-xs text-dore underline whitespace-nowrap hover:text-noir">Voir détails →</Link>
              </div>
            </div>
          ))}
        </div>
      )}

      <h2 className="font-display text-xl text-encre mb-4">Ma liste de souhaits</h2>
      {wishProducts.length === 0 ? (
        <p className="text-ardoise text-sm">Aucun favori pour le moment.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {wishProducts.map((p) => (
            <Link key={p.slug} to={`/produit/${p.slug}`} className="group">
              <div className="overflow-hidden hover-zoom aspect-[3/4]"><img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" /></div>
              <p className="text-xs text-encre mt-2 truncate">{p.name}</p>
              <p className="text-dore text-sm">{eur(p.price)}</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Account() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-[50vh] flex items-center justify-center text-ardoise">Chargement...</div>;
  return user ? <Dashboard /> : <AuthForms />;
}
